// create a context for the registration setup

import {
  Currency,
  RegistrationTypes,
  Tax,
} from '@sportninja/common/types/Registration'
import dayjs from 'dayjs'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useRegistrationData } from '@sportninja/common/hooks/useRegistrationData'
import { RegistrationTableDivision } from '../../PlayerRegistration/helpers'

const DivisionSetupContext = createContext<
  DivisionSetupContextType | undefined
>(undefined)

const useDivisionSetupContext = () => {
  const context = useContext(DivisionSetupContext)
  if (!context) {
    throw new Error(
      'useDivisionSetupContext must be used within a DivisionSetupContextWrapper'
    )
  }
  return context
}

const frequencyOptions = [
  { label: 'Weekly', value: '0' },
  { label: 'Bi-Weekly', value: '1' },
  { label: 'Monthly', value: '2' },
  { label: 'Bi-Monthly', value: '3' },
  { label: 'Quarterly', value: '4' },
  { label: 'Semi-Annually', value: '5' },
  { label: 'Annually', value: '6' },
]

type DivisionSetupState = {
  divisionName: string
  divisionType: string
  divisionAge: string
  daysOfWeek: string
  gender: string
  maxPlayersPerTeam: number | null
  maxTeams: number | null
  oneTimePayment: boolean
  currency: number
  tax: number
  requireDeposit: boolean
  requirePayment: boolean
  playerRequirePayment: boolean
  numberOfInstallments: number | null
  installmentFrequency: string | null
  installmentStartDate: string | null
  customQuestions: {
    [key: string]: boolean
  }
  waivers: string[]
  costPerRegistration: number | null
  deposit: number | null
  loading: boolean
  fieldErrors: string[]
  customError: any
  playerOneTimePayment: boolean
  playerCostPerRegistration: number | null
  playerDeposit: number | null
  playerNumberOfInstallments: number | null
  playerInstallmentFrequency: string | null
  playerInstallmentStartDate: string | null
  playerRequireDeposit: boolean
  enableMaxPlayers: boolean | null
  enableMaxTeams: boolean | null
  enableMaxPlayersPerBucket: boolean | null
  maxPlayersPerBucket: number | null
  taxes: string[]
}

type DivisionSetupContextType = {
  state: DivisionSetupState
  setState: React.Dispatch<React.SetStateAction<DivisionSetupState>>
  submitDivisionRegistration: () => Promise<void>
  division: RegistrationTableDivision | undefined
  isUpdate: boolean
  clearState: () => void
  setCustomErroByField: (field: string, message: string) => void
  types: RegistrationTypes
  availableTaxes: Tax[]
}

type DivisionSetupContextWrapperProps = {
  children: React.ReactNode
  division?: RegistrationTableDivision
  scheduleCurrency?: Currency
  types?: RegistrationTypes
  setSelectedDivision: (division: RegistrationTableDivision | null) => void
  playerRegistrationEnabled: boolean
  teamRegistrationEnabled: boolean
  availableTaxes: Tax[]
}

const DivisionSetupContextWrapper = ({
  children,
  division,
  scheduleCurrency,
  types,
  setSelectedDivision,
  playerRegistrationEnabled,
  teamRegistrationEnabled,
  availableTaxes,
}: DivisionSetupContextWrapperProps) => {
  const isUpdate = useMemo(
    () => division?.registration !== null,
    [division?.registration]
  )

  const [state, setState] = useState<DivisionSetupState>({
    divisionName: division?.name || '',
    divisionType: '1',
    divisionAge: '1',
    daysOfWeek: '1',
    gender: types?.gender?.[0]?.id || '1',
    maxPlayersPerTeam: null,
    maxTeams: null,
    oneTimePayment: false,
    currency: scheduleCurrency?.id ? Number(scheduleCurrency.id) : 0,
    tax: 0,
    requireDeposit: false,
    requirePayment: division?.registration?.team_cost !== null,
    numberOfInstallments: 1,
    installmentFrequency: '0',
    installmentStartDate: dayjs().utc().format('YYYY-MM-DD'),
    customQuestions: {},
    waivers: [],
    costPerRegistration: null,
    deposit: null,
    loading: false,
    fieldErrors: [],
    customError: '',
    // Same payment states that we have for team, but now for player
    playerOneTimePayment: false,
    playerCostPerRegistration: null,
    playerDeposit: null,
    playerNumberOfInstallments: 1,
    playerInstallmentFrequency: '0',
    playerInstallmentStartDate: dayjs().utc().format('YYYY-MM-DD'),
    playerRequireDeposit: false,
    playerRequirePayment: division?.registration?.player_cost !== null,
    enableMaxPlayers: !isUpdate
      ? false
      : division?.maxPlayers === null
      ? false
      : true,
    enableMaxTeams: !isUpdate
      ? false
      : division?.maxTeams === null
      ? false
      : true,
    enableMaxPlayersPerBucket: !isUpdate
      ? false
      : division?.registration?.max_players_per_bucket === null
      ? false
      : true,
    maxPlayersPerBucket: division?.registration?.max_players_per_bucket || null,
    taxes: [],
  })

  useEffect(() => {
    if (!isUpdate) {
      setState((prev) => ({
        ...prev,
        enableMaxPlayers: false,
        enableMaxTeams: false,
        enableMaxPlayersPerBucket: false,
      }))
    }
  }, [isUpdate])

  const { createDivisionRegistration, updateDivisionRegistration } =
    useRegistrationData('')

  const setCustomErroByField = (field: string, message: string) => {
    setState((prev) => ({ ...prev, fieldErrors: [...prev.fieldErrors, field] }))
    setState((prev) => ({
      ...prev,
      customError: { ...prev.customError, [field]: message },
    }))
  }

  const setCustomErrors = (invalid_fields: any) => {
    if (invalid_fields?.player_installments) {
      setCustomErroByField(
        'playerInstallments',
        invalid_fields.player_installments
      )
    }
    if (invalid_fields.day_of_week) {
      setCustomErroByField('day_of_week', invalid_fields.day_of_week)
    }
    if (invalid_fields.installments) {
      setCustomErroByField('installments', invalid_fields.installments)
    }
    if (invalid_fields.installment_frequency) {
      setCustomErroByField(
        'installment_frequency',
        invalid_fields.installment_frequency
      )
    }
  }
  const submitDivisionRegistration = async () => {
    try {
      setState((prev) => ({
        ...prev,
        fieldErrors: [],
        customError: '',
        loading: true,
      }))
      if (state?.enableMaxPlayersPerBucket) {
        if (
          state?.maxPlayersPerBucket === null ||
          state?.maxPlayersPerBucket === 0 ||
          state?.maxPlayersPerBucket === undefined ||
          isNaN(Number(state?.maxPlayersPerBucket))
        ) {
          setCustomErroByField(
            'maxPlayersPerBucket',
            'Max players per bucket is required'
          )
          const error = new Error('Max players per bucket is required')
          // @ts-ignore
          error.invalid_fields = {
            maxPlayersPerBucket: 'Max players per bucket is required',
          }
          throw error
        }
      }
      if (state?.enableMaxPlayers) {
        if (
          state?.maxPlayersPerTeam === null ||
          state?.maxPlayersPerTeam === 0 ||
          state?.maxPlayersPerTeam === undefined ||
          isNaN(Number(state?.maxPlayersPerTeam))
        ) {
          setCustomErroByField(
            'maxPlayersPerTeam',
            'Max players per team is required'
          )
          const error = new Error('Max players per team is required')
          // @ts-ignore
          error.invalid_fields = {
            maxPlayersPerTeam: 'Max players per team is required',
          }
          throw error
        }
      }
      if (state?.enableMaxTeams) {
        if (
          state?.maxTeams === null ||
          state?.maxTeams === 0 ||
          state?.maxTeams === undefined ||
          isNaN(Number(state?.maxTeams))
        ) {
          setCustomErroByField('maxTeams', 'Max teams is required')
          const error = new Error('Max teams is required')
          // @ts-ignore
          error.invalid_fields = {
            maxTeams: 'Max teams is required',
          }
          throw error
        }
      }

      if (
        teamRegistrationEnabled &&
        state?.requirePayment &&
        (state?.costPerRegistration === null ||
          state?.costPerRegistration === 0 ||
          isNaN(Number(state?.costPerRegistration)))
      ) {
        setCustomErroByField(
          'costPerRegistration',
          'Cost per registration is required'
        )
        const error = new Error('Cost per registration is required')
        // @ts-ignore
        error.invalid_fields = {
          costPerRegistration: 'Cost per registration is required',
        }
        throw error
      }
      if (
        teamRegistrationEnabled &&
        state?.requireDeposit &&
        (state?.deposit === null ||
          state?.deposit === 0 ||
          isNaN(Number(state?.deposit)) ||
          state?.deposit === undefined)
      ) {
        setCustomErroByField('deposit', 'Deposit amount is required')
        const error = new Error('Deposit amount is required')
        // @ts-ignore
        error.invalid_fields = {
          deposit: 'Deposit amount is required',
        }
        throw error
      }
      if (
        playerRegistrationEnabled &&
        state?.playerRequirePayment &&
        (state?.playerCostPerRegistration === null ||
          state?.playerCostPerRegistration === 0 ||
          isNaN(Number(state?.playerCostPerRegistration)) ||
          state?.playerCostPerRegistration === undefined)
      ) {
        setCustomErroByField(
          'playerCostPerRegistration',
          'Cost per registration is required'
        )
        const error = new Error('Cost per registration is required')
        // @ts-ignore
        error.invalid_fields = {
          playerCostPerRegistration: 'Cost per registration is required',
        }
        throw error
      }
      if (
        playerRegistrationEnabled &&
        state?.playerRequireDeposit &&
        (state?.playerDeposit === null ||
          state?.playerDeposit === 0 ||
          isNaN(Number(state?.playerDeposit)) ||
          state?.playerDeposit === undefined)
      ) {
        setCustomErroByField('playerDeposit', 'Deposit amount is required')
        const error = new Error('Deposit amount is required')
        // @ts-ignore
        error.invalid_fields = {
          playerDeposit: 'Deposit amount is required',
        }
        throw error
      }
      const divisionRegistrationData = {
        scheduleId: division?.id || '',
        // We always use the schedule currency for the division, to ensure that we don't have
        // Divisions with different currencies
        currency: scheduleCurrency?.id ? Number(scheduleCurrency.id) : 0,
        dayOfWeek: Number(state.daysOfWeek),
        divisionGender:
          state.gender !== null
            ? Number(state.gender)
            : Number(types?.gender?.[0]?.id),
        maxTeams: state.enableMaxTeams
          ? state.maxTeams === 0
            ? null
            : state.maxTeams
          : null,
        deposit: state.oneTimePayment ? null : state.deposit,
        teamCost: state.costPerRegistration,
        divisionAge: Number(state.divisionAge),
        maxPlayersPerTeam: state.enableMaxPlayers
          ? state.maxPlayersPerTeam === 0
            ? null
            : state.maxPlayersPerTeam
          : null,
        maxPlayersPerBucket: state.enableMaxPlayersPerBucket
          ? state.maxPlayersPerBucket === 0
            ? null
            : state.maxPlayersPerBucket
          : null,
        singlePayment: state.oneTimePayment,
        maxTeamRepPerTeam: 1,
        installments: state.numberOfInstallments,
        installmentStartDate: state.oneTimePayment
          ? null
          : state.installmentStartDate,
        installmentFrequency: state.oneTimePayment
          ? null
          : state.numberOfInstallments && state.numberOfInstallments <= 1
          ? null
          : !state.numberOfInstallments
          ? null
          : frequencyOptions[
              Number(state.installmentFrequency)
            ]?.label?.toLowerCase(),
        depositRequired: state.oneTimePayment ? false : state.requireDeposit,
        playerInstallments: state.playerNumberOfInstallments,
        playerInstallmentFrequency: state.playerOneTimePayment
          ? null
          : state.playerNumberOfInstallments &&
            state.playerNumberOfInstallments <= 1
          ? null
          : !state.playerNumberOfInstallments
          ? null
          : frequencyOptions[
              Number(state.playerInstallmentFrequency)
            ]?.label?.toLowerCase(),
        playerInstallmentStartDate:
          state.playerInstallmentStartDate === ''
            ? null
            : state.playerInstallmentStartDate,
        playerDepositRequired: state.playerRequireDeposit,
        playerCostPerRegistration: state.playerCostPerRegistration,
        playerDeposit: state.playerDeposit,
        playerSinglePayment: state.playerOneTimePayment,
        defaultTaxes: state.taxes,
      }

      if (isUpdate) {
        await updateDivisionRegistration(divisionRegistrationData)
      } else {
        await createDivisionRegistration(divisionRegistrationData)
      }
    } catch (error: any) {
      if (error?.invalid_fields) {
        setCustomErrors(error?.invalid_fields)
        throw error
      }
      throw error
    } finally {
      setState((prev) => ({ ...prev, loading: false }))
    }
  }

  const clearState = useCallback(() => {
    setState({
      divisionName: '',
      divisionType: '1',
      divisionAge: '1',
      daysOfWeek: '1',
      gender: '1',
      maxPlayersPerTeam: 0,
      maxTeams: 0,
      oneTimePayment: false,
      currency: 0,
      tax: 0,
      requireDeposit: false,
      numberOfInstallments: 1,
      installmentFrequency: '0',
      installmentStartDate: dayjs().utc().format('YYYY-MM-DD'),
      customQuestions: {},
      waivers: [],
      costPerRegistration: 0,
      deposit: 0,
      loading: false,
      fieldErrors: [],
      customError: null,
      playerOneTimePayment: false,
      playerCostPerRegistration: 0,
      playerDeposit: 0,
      playerNumberOfInstallments: 1,
      playerInstallmentFrequency: '0',
      playerInstallmentStartDate: dayjs().utc().format('YYYY-MM-DD'),
      playerRequireDeposit: false,
      requirePayment: false,
      playerRequirePayment: true,
      enableMaxPlayers: false,
      enableMaxTeams: false,
      taxes: [],
      enableMaxPlayersPerBucket: false,
      maxPlayersPerBucket: null,
    })
    setSelectedDivision(null)
  }, [])

  useEffect(() => {
    if (isUpdate && division) {
      const paymentNotRequired = division?.registration?.team_cost === null
      const playerPaymentNotRequired =
        division?.registration?.player_cost === null

      setState((prevState) => ({
        ...prevState,
        divisionName: division.name || '',
        divisionAge:
          division?.registration?.division_age?.id || prevState.divisionAge,
        daysOfWeek:
          division.registration?.day_of_week?.id || prevState.daysOfWeek,
        gender: division.genderId || types?.gender?.[0]?.id || '1',
        maxPlayersPerTeam:
          division?.registration?.max_players_per_team ??
          prevState.maxPlayersPerTeam,
        maxTeams: division?.registration?.max_teams ?? prevState.maxTeams,
        enableMaxPlayers:
          division?.registration?.max_players_per_team === null ? false : true,
        enableMaxTeams:
          division?.registration?.max_teams === null ? false : true,
        // Team payment settings
        requirePayment: !paymentNotRequired,
        costPerRegistration: paymentNotRequired
          ? null
          : division.registration?.team_cost ?? prevState.costPerRegistration,
        requireDeposit: division.registration?.deposit_required ? true : false,
        deposit: paymentNotRequired
          ? null
          : division.registration?.deposit ?? prevState.deposit,
        oneTimePayment: paymentNotRequired
          ? false
          : division?.singlePayment ?? prevState.oneTimePayment,
        numberOfInstallments: paymentNotRequired
          ? null
          : division.registration?.installments ??
            prevState.numberOfInstallments,
        installmentFrequency: paymentNotRequired
          ? null
          : frequencyOptions?.find(
              (option) =>
                option?.label?.toLowerCase() ===
                division.registration?.installment_frequency?.toLowerCase()
            )?.value ?? prevState.installmentFrequency,
        installmentStartDate: paymentNotRequired
          ? null
          : division.registration?.installment_start_date ??
            prevState.installmentStartDate,
        // Player payment settings
        playerNumberOfInstallments: playerPaymentNotRequired
          ? null
          : division.registration?.player_installments ??
            prevState.playerNumberOfInstallments,
        playerInstallmentFrequency: playerPaymentNotRequired
          ? null
          : frequencyOptions?.find(
              (option) =>
                option?.label?.toLowerCase() ===
                division.registration?.player_installment_frequency?.toLowerCase()
            )?.value ?? prevState.playerInstallmentFrequency,
        playerInstallmentStartDate: playerPaymentNotRequired
          ? null
          : division.registration?.player_installment_start_date
          ? division.registration?.player_installment_start_date
          : prevState.playerInstallmentStartDate,
        playerRequirePayment: !playerPaymentNotRequired,
        playerCostPerRegistration: playerPaymentNotRequired
          ? null
          : division.registration?.player_cost ??
            prevState.playerCostPerRegistration,
        playerRequireDeposit: division.registration?.player_deposit_required
          ? true
          : false,
        playerDeposit: playerPaymentNotRequired
          ? null
          : division.registration?.player_deposit ?? prevState.playerDeposit,
        playerOneTimePayment: playerPaymentNotRequired
          ? false
          : division?.registration?.player_single_payment ??
            prevState.playerOneTimePayment,
        // @ts-ignore
        taxes: division?.registration?.taxes?.map((tax) => tax.id) || [],
        maxPlayersPerBucket:
          division?.registration?.max_players_per_bucket ??
          prevState.maxPlayersPerBucket,
        enableMaxPlayersPerBucket:
          division?.registration?.max_players_per_bucket === null
            ? false
            : true,
      }))
    }
  }, [division, isUpdate, types?.gender])

  const value = {
    state,
    setState,
    submitDivisionRegistration,
    division,
    isUpdate,
    clearState,
    setCustomErroByField,
    types: types || {},
    availableTaxes,
  }

  return (
    <DivisionSetupContext.Provider value={value}>
      {children}
    </DivisionSetupContext.Provider>
  )
}

export {
  DivisionSetupContext,
  useDivisionSetupContext,
  DivisionSetupContextWrapper,
}
